import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, FileText, Search, Upload, ArrowRight, Calendar, CheckCircle2, Circle, ListPlus, Loader2 } from 'lucide-react';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { toast } from 'sonner';
import { standaloneCompaniesAPI } from '../lib/api';
import PDFUploadModal from '../components/PDFUploadModal';
import PDFProgressTracker from '../components/PDFProgressTracker';
import { useResearchQueueStore } from '../stores/researchQueueStore';

type ResearchMode = 'sec' | 'pdf' | null;
type TimeRange = '1year' | '2years' | '3years' | '5years';

interface SECReport {
  form: string;
  accessionNumber: string;
  filingDate: string;
  primaryDocument: string;
  url: string;
  fiscalPeriod: string;
  alreadyProcessed?: boolean; // Indicates if this report has already been processed for this company
}

export default function NewCompanyResearch() {
  const navigate = useNavigate();
  const { addCompanyTask } = useResearchQueueStore();
  const [isAddingToQueue, setIsAddingToQueue] = useState(false);
  const [isStartingResearch, setIsStartingResearch] = useState(false);

  // Step 1: Select research mode
  const [researchMode, setResearchMode] = useState<ResearchMode>(null);

  // Step 2: SEC company search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Step 3: Time range selection (SEC only)
  const [timeRange, setTimeRange] = useState<TimeRange>('2years');

  // Step 4: Report selection (SEC only)
  const [availableReports, setAvailableReports] = useState<SECReport[]>([]);
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);

  // Step 5: PDF upload (PDF only) - Async with modal and progress tracking
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pendingPDF, setPendingPDF] = useState<File | null>(null); // PDF waiting for metadata in modal
  const [activePDFUploads, setActivePDFUploads] = useState<Array<{ uploadId: string; filename: string }>>([]);

  // Company selection (for adding to existing company)
  const [existingCompanies, setExistingCompanies] = useState<any[]>([]);
  const [selectedExistingCompany, setSelectedExistingCompany] = useState<any | null>(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [companySelectionMode, setCompanySelectionMode] = useState<'new' | 'existing'>('new');

  // Current step
  const [currentStep, setCurrentStep] = useState(1);

  // Auto-search function (without minimum length check - that's in useEffect)
  const performSearch = useCallback(async (query: string) => {
    setIsSearching(true);
    try {
      // Call real SEC search API
      const results = await standaloneCompaniesAPI.searchSECCompanies(query);

      // Transform API response to match our interface
      const transformedResults = results.map((company: any) => ({
        companyName: company.name,
        ticker: company.ticker,
        cik: company.cik,
        exchange: company.exchange,
      }));

      setSearchResults(transformedResults);

      if (transformedResults.length === 0) {
        toast.info('No companies found. Try a different search term.');
      }
    } catch (error) {
      toast.error('Failed to search companies');
      console.error(error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced auto-search effect
  useEffect(() => {
    // Only search if query is at least 4 characters
    if (searchQuery.trim().length < 4) {
      setSearchResults([]);
      return;
    }

    // Debounce: wait 500ms after user stops typing
    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery.trim());
    }, 500);

    // Cleanup: cancel previous timer if user keeps typing
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, performSearch]);

  // Load existing companies when research mode is selected
  useEffect(() => {
    if (researchMode) {
      loadExistingCompanies();
    }
  }, [researchMode]);

  const loadExistingCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const companies = await standaloneCompaniesAPI.listCompanies();
      setExistingCompanies(companies);
    } catch (error) {
      console.error('Failed to load existing companies:', error);
      toast.error('Failed to load existing companies');
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  // Manual search function (for search button - still useful for Enter key)
  const handleSearch = () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a company name or ticker');
      return;
    }
    if (searchQuery.trim().length < 4) {
      toast.error('Please enter at least 4 characters');
      return;
    }
    performSearch(searchQuery.trim());
  };

  const handleSelectCompany = (company: any) => {
    setSelectedCompany(company);
    setCurrentStep(3); // Go to time range selection first
  };

  // Fetch available reports when time range is selected
  const handleTimeRangeSelected = async (selectedTimeRange: TimeRange) => {
    setTimeRange(selectedTimeRange);

    if (!selectedCompany) return;

    setIsLoadingReports(true);
    try {
      toast.info('Fetching available reports from SEC...');

      const response = await standaloneCompaniesAPI.getAvailableReports(
        selectedCompany.cik,
        selectedTimeRange
      );

      // Fetch existing processed reports for this company
      let existingReports: any[] = [];
      if (selectedCompany.companyId) {
        try {
          const companyData = await standaloneCompaniesAPI.getCompany(selectedCompany.companyId);
          if (companyData.reportCount > 0) {
            const reportsData = await standaloneCompaniesAPI.getCompanyReports(selectedCompany.companyId);
            existingReports = reportsData.reports || [];
          }
        } catch (err) {
          console.warn('Could not fetch existing reports:', err);
        }
      }

      // Create a set of existing fiscal periods for quick lookup
      const existingFiscalPeriods = new Set(
        existingReports.map((r: any) => r.fiscalPeriod)
      );

      // Mark reports as already processed and pre-select only unprocessed ones
      const reportsWithStatus = (response.reports || []).map((report: SECReport) => ({
        ...report,
        alreadyProcessed: existingFiscalPeriods.has(report.fiscalPeriod)
      }));

      setAvailableReports(reportsWithStatus);

      // Pre-select only unprocessed reports by default
      const unprocessedReportIds = reportsWithStatus
        .filter((r: any) => !r.alreadyProcessed)
        .map((r: any) => r.accessionNumber);
      setSelectedReportIds(unprocessedReportIds);

      const totalCount = reportsWithStatus.length;
      const processedCount = totalCount - unprocessedReportIds.length;
      const newCount = unprocessedReportIds.length;

      if (processedCount > 0) {
        toast.success(
          `Found ${totalCount} reports (${processedCount} already processed, ${newCount} new)`,
          { duration: 4000 }
        );
      } else {
        toast.success(`Found ${totalCount} available reports`);
      }

      setCurrentStep(4); // Move to report selection step
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || 'Failed to fetch available reports';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setIsLoadingReports(false);
    }
  };

  // Toggle report selection
  const toggleReportSelection = (accessionNumber: string) => {
    setSelectedReportIds(prev => {
      if (prev.includes(accessionNumber)) {
        return prev.filter(id => id !== accessionNumber);
      } else {
        return [...prev, accessionNumber];
      }
    });
  };

  // Select all / deselect all
  const handleSelectAllReports = () => {
    if (selectedReportIds.length === availableReports.length) {
      setSelectedReportIds([]);
    } else {
      setSelectedReportIds(availableReports.map(r => r.accessionNumber));
    }
  };

  // Job polling state
  const [isPolling, setIsPolling] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<any>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);

  const handleStartResearch = async () => {
    console.log('[handleStartResearch] triggered, mode:', researchMode);
    if (researchMode === 'sec' && selectedCompany) {
      try {
        if (selectedReportIds.length === 0) {
          toast.error('Please select at least one report to process');
          return;
        }

        setIsStartingResearch(true);
        toast.info(`Starting research for ${selectedCompany.companyName}...`);

        // Fire US financial data pipeline in background (non-blocking, fire-and-forget)
        // This fetches XBRL → runs calculator → saves to MongoDB/Neo4j
        // It powers the Finance / EV / Revenue / Sensitivity tabs
        standaloneCompaniesAPI.fetchUSFinancialData({
          company_name: selectedCompany.companyName,
          company_code: selectedCompany.ticker,
          time_range: timeRange,
        }).then(() => {
          toast.success(`Financial data pipeline completed for ${selectedCompany.companyName}`);
        }).catch((usError: any) => {
          console.warn('[US Financial Data] Background pipeline error:', usError);
        });

        // Call SEC research API with selected reports (returns 202 with jobId)
        const response = await standaloneCompaniesAPI.startResearch({
          cik: selectedCompany.cik,
          companyName: selectedCompany.companyName,
          ticker: selectedCompany.ticker,
          selectedReports: selectedReportIds,
          companyId: companySelectionMode === 'existing' ? selectedExistingCompany?.companyId : undefined,
        });

        // Store job ID and start polling
        setJobId(response.jobId);
        setShowProgressModal(true);
        setIsPolling(true);
        setIsStartingResearch(false);

        toast.success(`Research job started! Processing ${selectedReportIds.length} reports.`);

        // Start polling for job status
        pollJobStatus(response.jobId, response.companyId);

      } catch (error: any) {
        const errorMsg = error?.response?.data?.detail || 'Failed to start research';
        toast.error(errorMsg);
        console.error(error);
        setIsStartingResearch(false);
      }
    } else if (researchMode === 'pdf' && uploadedFile) {
      // PDF files are now handled via async upload with modal
      // Show the modal to collect metadata
      setPendingPDF(uploadedFile);
    }
  };

  // Add to research queue instead of starting immediately
  const handleAddToQueue = async () => {
    if (researchMode === 'sec' && selectedCompany) {
      if (selectedReportIds.length === 0) {
        toast.error('Please select at least one report to process');
        return;
      }

      setIsAddingToQueue(true);
      try {
        await addCompanyTask({
          companyName: selectedCompany.companyName,
          cik: selectedCompany.cik,
          ticker: selectedCompany.ticker,
          timeRange: timeRange,
          companyId: companySelectionMode === 'existing' ? selectedExistingCompany?.companyId : undefined,
        });

        toast.success(
          `Added "${selectedCompany.companyName}" to research queue`,
          { description: `${selectedReportIds.length} reports will be processed when queue starts` }
        );

        // Navigate to queue page
        navigate('/research-queue');
      } catch (error: any) {
        const errorMsg = error?.response?.data?.detail || 'Failed to add to queue';
        toast.error(errorMsg);
        console.error(error);
      } finally {
        setIsAddingToQueue(false);
      }
    }
  };

  // Handle PDF upload with async flow (modal → upload → progress tracking)
  const handlePDFMetadataSubmit = async (metadata: {
    companyName: string;
    fiscalYear?: number;
    ticker?: string;
  }) => {
    if (!pendingPDF) return;

    try {
      toast.info('Starting PDF extraction...');

      // Start async upload
      const response = await standaloneCompaniesAPI.uploadPDFAsync({
        file: pendingPDF,
        companyName: metadata.companyName,
        fiscalYear: metadata.fiscalYear,
        ticker: metadata.ticker,
        companyId: companySelectionMode === 'existing' ? selectedExistingCompany?.companyId : undefined,
      });

      // Add to active uploads for progress tracking
      setActivePDFUploads(prev => [...prev, {
        uploadId: response.uploadId,
        filename: pendingPDF.name
      }]);

      // Clear pending PDF
      setPendingPDF(null);

      toast.success('PDF upload started! Processing in background...');

    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || 'Failed to start PDF upload';
      toast.error(errorMsg);
      console.error(error);
      setPendingPDF(null);
    }
  };

  const handlePDFComplete = (uploadId: string, companyId: string, reportId: string) => {
    console.log('PDF extraction completed:', { uploadId, companyId, reportId });

    toast.success('PDF extraction completed successfully!');

    // Navigate to company detail page after a brief delay
    setTimeout(() => {
      navigate(`/companies/${companyId}`);
    }, 1500);
  };

  const handlePDFError = (uploadId: string, error: string) => {
    console.error('PDF extraction failed:', { uploadId, error });
    toast.error(`PDF extraction failed: ${error}`);
  };

  const pollJobStatus = async (jobId: string, companyId: string) => {
    const pollInterval = 2000; // Poll every 2 seconds
    let attempts = 0;
    const maxAttempts = 300; // 10 minutes max (300 * 2s = 600s)

    const poll = async () => {
      try {
        attempts++;

        // Get job status
        const status = await standaloneCompaniesAPI.getJobStatus(jobId);
        setJobStatus(status);

        console.log(`[Poll ${attempts}] Job status:`, status.status, `Progress: ${status.progress.percentage}%`);

        // Check if job is complete
        if (status.status === 'completed') {
          setIsPolling(false);
          setShowProgressModal(false);
          toast.success(`Research completed! Processed ${status.progress.completed} reports.`);

          // Navigate to company detail page
          setTimeout(() => {
            navigate(`/companies/${companyId}`);
          }, 1000);
          return;
        }

        // Check if job failed
        if (status.status === 'failed') {
          setIsPolling(false);
          toast.error(`Research failed: ${status.error || 'Unknown error'}`);

          // Show retry option if available
          if (status.canRetry) {
            toast.info('You can retry this job from the company detail page.');
          }
          return;
        }

        // Continue polling if still in progress or queued
        if ((status.status === 'in_progress' || status.status === 'queued') && attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else if (attempts >= maxAttempts) {
          setIsPolling(false);
          toast.error('Job polling timed out. Check company detail page for status.');
        }

      } catch (error: any) {
        console.error('Error polling job status:', error);

        // Continue polling unless we've exceeded max attempts
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setIsPolling(false);
          toast.error('Failed to get job status. Check company detail page.');
        }
      }
    };

    // Start polling
    poll();
  };

  const timeRangeOptions = [
    { value: '1year' as TimeRange, label: '1 Year', periods: '4 quarterly reports' },
    { value: '2years' as TimeRange, label: '2 Years', periods: '8 quarterly reports' },
    { value: '3years' as TimeRange, label: '3 Years', periods: '12 quarterly reports' },
    { value: '5years' as TimeRange, label: '5 Years', periods: '20 quarterly reports' },
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/companies')}
          className="mb-4"
        >
          ← Back to Companies
        </Button>
        <h1 className="text-3xl font-bold text-neutral-900 flex items-center gap-3">
          <Building2 className="h-8 w-8 text-primary-600" />
          Research New Company
        </h1>
        <p className="text-neutral-600 mt-2">
          Analyze company financials, operations, and strategy using AI-powered research
        </p>
      </div>

      {/* Company Selection Mode */}
      {researchMode && (
        <Card className="mb-6">
          <CardBody className="p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Company Selection</h3>

            <div className="flex gap-4 mb-4">
              <button
                onClick={() => {
                  setCompanySelectionMode('new');
                  setSelectedExistingCompany(null);
                }}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  companySelectionMode === 'new'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <div className="font-semibold text-neutral-900 mb-1">Create New Company</div>
                <div className="text-sm text-neutral-600">Start research for a company not yet in the system</div>
              </button>

              <button
                onClick={() => setCompanySelectionMode('existing')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  companySelectionMode === 'existing'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <div className="font-semibold text-neutral-900 mb-1">Add to Existing Company</div>
                <div className="text-sm text-neutral-600">Add reports to a company already in the system</div>
              </button>
            </div>

            {/* Existing Company Selector */}
            {companySelectionMode === 'existing' && (
              <div className="mt-4">
                {isLoadingCompanies ? (
                  <div className="text-center py-8 text-neutral-600">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                    Loading companies...
                  </div>
                ) : existingCompanies.length === 0 ? (
                  <div className="text-center py-8 text-neutral-600">
                    No existing companies found. Please create a new company.
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Select Company
                    </label>
                    <select
                      value={selectedExistingCompany?.companyId || ''}
                      onChange={(e) => {
                        const company = existingCompanies.find(c => c.companyId === e.target.value);
                        setSelectedExistingCompany(company || null);
                      }}
                      className="w-full p-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a company...</option>
                      {existingCompanies.map((company) => (
                        <option key={company.companyId} value={company.companyId}>
                          {company.companyName} {company.ticker ? `(${company.ticker})` : ''} - {company.reportCount || 0} reports
                        </option>
                      ))}
                    </select>

                    {selectedExistingCompany && (
                      <div className="mt-3 p-3 bg-neutral-50 rounded-lg">
                        <div className="text-sm font-medium text-neutral-900 mb-1">
                          {selectedExistingCompany.companyName}
                        </div>
                        <div className="text-xs text-neutral-600 space-y-1">
                          {selectedExistingCompany.ticker && (
                            <div>Ticker: {selectedExistingCompany.ticker}</div>
                          )}
                          <div>Reports: {selectedExistingCompany.reportCount || 0}</div>
                          {selectedExistingCompany.latestPeriod && (
                            <div>Latest Period: {selectedExistingCompany.latestPeriod}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Step 1: Select Research Mode */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 mb-4">
              Select Research Source
            </h2>
            <p className="text-neutral-600 mb-6">
              Choose how you want to research this company
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* SEC Company Option */}
            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                researchMode === 'sec' ? 'ring-2 ring-primary-500' : ''
              }`}
              onClick={() => {
                setResearchMode('sec');
                setCurrentStep(2);
              }}
            >
              <CardBody className="text-center p-8">
                <div className="w-16 h-16 rounded-full bg-primary-100 mx-auto flex items-center justify-center mb-4">
                  <FileText className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  SEC Public Company
                </h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Automatically fetch and analyze 10-K/10-Q filings from SEC EDGAR
                </p>
                <div className="space-y-2 text-sm text-neutral-700">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span>Auto-fetch filings</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span>Historical data (1-5 years)</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span>XBRL structured data</span>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* PDF Upload Option */}
            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                researchMode === 'pdf' ? 'ring-2 ring-primary-500' : ''
              }`}
              onClick={() => {
                setResearchMode('pdf');
                setCurrentStep(2); // Move to PDF upload step
              }}
            >
              <CardBody className="text-center p-8">
                <div className="w-16 h-16 rounded-full bg-secondary-100 mx-auto flex items-center justify-center mb-4">
                  <Upload className="h-8 w-8 text-secondary-600" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  Private Company (PDF)
                </h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Upload annual reports, financial statements, or investor presentations
                </p>
                <div className="space-y-2 text-sm text-neutral-700">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                    <span>Upload PDFs</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                    <span>AI-powered extraction</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span>Manual classification</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Step 2: SEC Company Search */}
      {currentStep === 2 && researchMode === 'sec' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              Search SEC Company
            </h2>
            <p className="text-neutral-600 mb-6">
              Find a public company by name or ticker symbol
            </p>
          </div>

          <Card>
            <CardBody>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      placeholder="Start typing company name or ticker (min 4 characters)..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      leftIcon={<Search className="h-4 w-4 text-neutral-400" />}
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    loading={isSearching}
                    disabled={isSearching || searchQuery.trim().length < 4}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </div>

                {/* Helpful hint */}
                <div className="flex items-start gap-2 text-sm text-neutral-600">
                  <div className="text-primary-500 mt-0.5">ℹ️</div>
                  <p>
                    {searchQuery.trim().length === 0 && "Type at least 4 characters to start auto-search"}
                    {searchQuery.trim().length > 0 && searchQuery.trim().length < 4 && `${4 - searchQuery.trim().length} more character${4 - searchQuery.trim().length === 1 ? '' : 's'} needed`}
                    {searchQuery.trim().length >= 4 && isSearching && "Searching SEC database..."}
                    {searchQuery.trim().length >= 4 && !isSearching && searchResults.length > 0 && `Found ${searchResults.length} ${searchResults.length === 1 ? 'company' : 'companies'}`}
                    {searchQuery.trim().length >= 4 && !isSearching && searchResults.length === 0 && "No companies found"}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-neutral-700">
                Search Results ({searchResults.length})
              </h3>
              {searchResults.map((company) => (
                <Card
                  key={company.cik}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleSelectCompany(company)}
                >
                  <CardBody>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-neutral-900">
                          {company.companyName}
                        </h4>
                        <div className="flex items-center gap-3 text-sm text-neutral-600 mt-1">
                          <span className="font-medium text-primary-600">
                            {company.ticker}
                          </span>
                          <span>CIK: {company.cik}</span>
                          <span className="px-2 py-0.5 bg-neutral-100 rounded text-xs">
                            {company.exchange}
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-neutral-400" />
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2: PDF Upload Form (PDF mode) */}
      {currentStep === 2 && researchMode === 'pdf' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              Upload Annual Report PDF
            </h2>
            <p className="text-neutral-600 mb-6">
              Upload a company's annual report or financial statements (PDF format)
            </p>
          </div>

          {/* Active PDF Upload Progress Trackers */}
          {activePDFUploads.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-neutral-700">
                Active PDF Extractions ({activePDFUploads.length})
              </h3>
              {activePDFUploads.map(upload => (
                <PDFProgressTracker
                  key={upload.uploadId}
                  uploadId={upload.uploadId}
                  filename={upload.filename}
                  onComplete={(companyId, reportId) => handlePDFComplete(upload.uploadId, companyId, reportId)}
                  onError={(error) => handlePDFError(upload.uploadId, error)}
                />
              ))}
            </div>
          )}

          <Card>
            <CardBody className="space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Annual Report PDF <span className="text-red-500">*</span>
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    uploadedFile
                      ? 'border-green-500 bg-green-50'
                      : 'border-neutral-300 hover:border-primary-500 hover:bg-primary-50'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const files = e.dataTransfer.files;
                    if (files.length > 0 && files[0].type === 'application/pdf') {
                      const file = files[0];
                      setUploadedFile(file);
                      setPendingPDF(file); // Immediately show modal
                    } else {
                      toast.error('Please upload a PDF file');
                    }
                  }}
                >
                  {uploadedFile ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-center">
                        <FileText className="h-12 w-12 text-green-600" />
                      </div>
                      <div>
                        <div className="text-green-900 font-medium">{uploadedFile.name}</div>
                        <div className="text-sm text-green-700">
                          {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUploadedFile(null);
                          setPendingPDF(null);
                        }}
                      >
                        Remove File
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-center">
                        <Upload className="h-12 w-12 text-neutral-400" />
                      </div>
                      <div>
                        <div className="text-neutral-900 font-medium mb-1">
                          Drag & drop your PDF here
                        </div>
                        <div className="text-sm text-neutral-600 mb-3">or</div>
                        <label htmlFor="pdf-upload">
                          <input
                            id="pdf-upload"
                            type="file"
                            accept=".pdf,application/pdf"
                            className="hidden"
                            onChange={(e) => {
                              const files = e.target.files;
                              if (files && files.length > 0) {
                                const file = files[0];
                                setUploadedFile(file);
                                setPendingPDF(file); // Show modal for metadata
                              }
                            }}
                          />
                          <Button
                            variant="primary"
                            size="sm"
                            as="span"
                          >
                            Browse Files
                          </Button>
                        </label>
                      </div>
                      <div className="text-xs text-neutral-500">
                        Supported format: PDF (max 50 MB)
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setResearchMode(null);
                    setCurrentStep(1);
                    setUploadedFile(null);
                    setPendingPDF(null);
                  }}
                >
                  Back
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Step 3: Time Range Selection */}
      {currentStep === 3 && researchMode === 'sec' && selectedCompany && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              Select Time Range
            </h2>
            <p className="text-neutral-600 mb-2">
              How far back should we analyze?
            </p>
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
              <div className="text-blue-600 mt-0.5">
                <Building2 className="h-4 w-4" />
              </div>
              <div className="text-sm text-blue-900">
                <span className="font-semibold">{selectedCompany.companyName}</span>
                <span className="text-blue-700 ml-2">({selectedCompany.ticker})</span>
              </div>
            </div>
          </div>

          <div className="grid gap-4">
            {timeRangeOptions.map((option) => (
              <Card
                key={option.value}
                className={`cursor-pointer transition-all ${
                  timeRange === option.value
                    ? 'ring-2 ring-primary-500 bg-primary-50'
                    : 'hover:shadow-md'
                }`}
                onClick={() => handleTimeRangeSelected(option.value)}
              >
                <CardBody className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      timeRange === option.value
                        ? 'border-primary-600 bg-primary-600'
                        : 'border-neutral-300'
                    }`}>
                      {timeRange === option.value && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900">
                        {option.label}
                      </div>
                      <div className="text-sm text-neutral-600">
                        {option.periods}
                      </div>
                    </div>
                  </div>
                  <Calendar className="h-5 w-5 text-neutral-400" />
                </CardBody>
              </Card>
            ))}
          </div>

          {isLoadingReports && (
            <div className="flex items-center justify-center gap-3 p-8 bg-blue-50 rounded-lg mt-6">
              <div className="animate-spin">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
              </div>
              <span className="text-blue-900 font-medium">Fetching available reports from SEC...</span>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Report Selection */}
      {currentStep === 4 && researchMode === 'sec' && selectedCompany && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              Select Reports to Process
            </h2>
            <p className="text-neutral-600 mb-2">
              Choose which SEC filings you want to analyze
            </p>
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
              <div className="text-blue-600 mt-0.5">
                <Building2 className="h-4 w-4" />
              </div>
              <div className="text-sm text-blue-900">
                <span className="font-semibold">{selectedCompany.companyName}</span>
                <span className="text-blue-700 ml-2">({selectedCompany.ticker})</span>
                <span className="text-blue-700 ml-2">• {timeRange}</span>
              </div>
            </div>
          </div>

          {/* Select All / Deselect All */}
          <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
            <span className="text-sm font-medium text-neutral-700">
              {selectedReportIds.length} of {availableReports.length} reports selected
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAllReports}
            >
              {selectedReportIds.length === availableReports.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          {/* Available Reports List */}
          <div className="space-y-3">
            {availableReports.map((report) => {
              const isSelected = selectedReportIds.includes(report.accessionNumber);
              const alreadyProcessed = report.alreadyProcessed || false;
              return (
                <Card
                  key={report.accessionNumber}
                  className={`cursor-pointer transition-all ${
                    isSelected
                      ? 'ring-2 ring-primary-500 bg-primary-50'
                      : alreadyProcessed
                      ? 'bg-green-50 border-green-200 opacity-75'
                      : 'hover:shadow-md'
                  }`}
                  onClick={() => toggleReportSelection(report.accessionNumber)}
                >
                  <CardBody>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 flex-1">
                        {/* Checkbox */}
                        <div className="mt-1">
                          {isSelected ? (
                            <CheckCircle2 className="h-5 w-5 text-primary-600" />
                          ) : (
                            <Circle className="h-5 w-5 text-neutral-400" />
                          )}
                        </div>

                        {/* Report Details */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              report.form === '10-K'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {report.form}
                            </span>
                            <span className="font-semibold text-neutral-900">
                              {report.fiscalPeriod}
                            </span>
                            {alreadyProcessed && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                Already Processed
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-neutral-600 space-y-1">
                            <div>
                              <span className="font-medium">Filed:</span>{' '}
                              {new Date(report.filingDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </div>
                            <div className="text-xs text-neutral-500">
                              Accession: {report.accessionNumber}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* SEC Link */}
                      <a
                        href={report.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary-600 hover:text-primary-700 mt-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View on SEC →
                      </a>
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(3)}
            >
              Back
            </Button>
            <Button
              variant="outline"
              onClick={handleAddToQueue}
              disabled={selectedReportIds.length === 0 || isAddingToQueue}
              title="Add to background queue for batch processing"
            >
              {isAddingToQueue ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <ListPlus className="h-4 w-4 mr-2" />
                  Add to Queue
                </>
              )}
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleStartResearch}
              disabled={selectedReportIds.length === 0 || isStartingResearch}
            >
              {isStartingResearch ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Start Research ({selectedReportIds.length} {selectedReportIds.length === 1 ? 'report' : 'reports'})
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {showProgressModal && jobStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl m-4">
            <CardHeader>
              <h2 className="text-xl font-semibold text-neutral-900">
                Research In Progress
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                Processing SEC filings for {jobStatus.companyName}
              </p>
            </CardHeader>
            <CardBody>
              <div className="space-y-6">
                {/* Overall Progress */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-neutral-700">
                      Overall Progress
                    </span>
                    <span className="text-sm font-semibold text-primary-600">
                      {jobStatus.progress.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-2.5">
                    <div
                      className="bg-primary-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${jobStatus.progress.percentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-neutral-600 mt-2">
                    <span>{jobStatus.progress.completed} completed</span>
                    <span>{jobStatus.progress.failed} failed</span>
                    <span>{jobStatus.progress.total} total</span>
                  </div>
                </div>

                {/* Current Phase */}
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <div className="animate-spin">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-blue-900">
                      {jobStatus.currentPhase === 'fetching_reports' && 'Fetching Reports'}
                      {jobStatus.currentPhase === 'processing_reports' && 'Processing Reports'}
                      {jobStatus.currentPhase === 'generating_summary' && 'Generating Summary'}
                      {jobStatus.currentPhase === 'planning' && 'Planning Research'}
                    </div>
                    <div className="text-xs text-blue-700">
                      {jobStatus.status === 'queued' && 'Job queued, will start shortly...'}
                      {jobStatus.status === 'in_progress' && 'Processing in background...'}
                    </div>
                  </div>
                </div>

                {/* Report Status List */}
                {jobStatus.reports && jobStatus.reports.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-neutral-700 mb-3">
                      Report Status
                    </h3>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {jobStatus.reports.map((report: any, idx: number) => {
                        // Parse currentProgress if it exists (stored as JSON string in Neo4j)
                        let currentProgress = null;
                        if (report.currentProgress) {
                          try {
                            currentProgress = typeof report.currentProgress === 'string'
                              ? JSON.parse(report.currentProgress)
                              : report.currentProgress;
                          } catch (e) {
                            console.error('Failed to parse progress:', e);
                          }
                        }

                        return (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg border transition-all ${
                              report.status === 'in_progress'
                                ? 'bg-blue-50 border-blue-200'
                                : report.status === 'completed'
                                ? 'bg-green-50 border-green-200'
                                : report.status === 'failed'
                                ? 'bg-red-50 border-red-200'
                                : 'bg-neutral-50 border-neutral-200'
                            }`}
                          >
                            {/* Report Header */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-2 h-2 rounded-full ${
                                    report.status === 'completed'
                                      ? 'bg-green-500'
                                      : report.status === 'failed'
                                      ? 'bg-red-500'
                                      : report.status === 'in_progress'
                                      ? 'bg-blue-500 animate-pulse'
                                      : 'bg-neutral-300'
                                  }`}
                                ></div>
                                <div className="text-sm font-medium text-neutral-900">
                                  {report.formType} - {report.fiscalPeriod}
                                </div>
                              </div>
                              <div className="text-xs text-neutral-500">
                                {report.attempts > 0 && `Attempt ${report.attempts}`}
                              </div>
                            </div>

                            {/* Detailed Progress - Only show for in_progress reports */}
                            {report.status === 'in_progress' && currentProgress && (
                              <div className="space-y-2">
                                {/* Progress Message */}
                                <div className="text-xs text-neutral-700 font-medium">
                                  {currentProgress.message}
                                </div>

                                {/* Progress Bar - if percentage available */}
                                {currentProgress.percentage !== null && currentProgress.percentage !== undefined && (
                                  <div>
                                    <div className="w-full bg-neutral-200 rounded-full h-1.5">
                                      <div
                                        className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                        style={{ width: `${currentProgress.percentage}%` }}
                                      ></div>
                                    </div>
                                    <div className="text-xs text-neutral-600 mt-1">
                                      {currentProgress.percentage}%
                                    </div>
                                  </div>
                                )}

                                {/* Sub-steps (e.g., page progress) */}
                                {currentProgress.currentStep !== null && currentProgress.totalSteps !== null && (
                                  <div className="text-xs text-neutral-600">
                                    Step {currentProgress.currentStep} of {currentProgress.totalSteps}
                                  </div>
                                )}

                                {/* Timestamp */}
                                {currentProgress.timestamp && (
                                  <div className="text-xs text-neutral-500">
                                    Updated: {new Date(currentProgress.timestamp).toLocaleTimeString()}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Error Message */}
                            {report.error && (
                              <div className="text-xs text-red-600 mt-2 p-2 bg-red-100 rounded">
                                {report.error}
                              </div>
                            )}

                            {/* Completed Status */}
                            {report.status === 'completed' && (
                              <div className="text-xs text-green-600 mt-1">
                                ✓ Processing complete
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Job Info */}
                <div className="flex justify-between text-xs text-neutral-600 pt-4 border-t">
                  <span>Job ID: {jobStatus.jobId}</span>
                  {jobStatus.timestamps.started && (
                    <span>
                      Started: {new Date(jobStatus.timestamps.started).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* PDF Upload Modal */}
      {pendingPDF && (
        <PDFUploadModal
          file={pendingPDF}
          onSubmit={handlePDFMetadataSubmit}
          onCancel={() => setPendingPDF(null)}
        />
      )}
    </div>
  );
}

